import React from "react";
import { Users, ShieldCheck, Crown, Gift } from "lucide-react";

interface ReferralMilestonesProps {
  referralCount: number;
}

const ReferralMilestones: React.FC<ReferralMilestonesProps> = ({ referralCount }) => {
  const milestones = [
    { count: 30, label: "3 Days PRO", icon: <Gift size={16} /> },
    { count: 100, label: "1 Week PRO", icon: <ShieldCheck size={16} /> },
    { count: 500, label: "LIFETIME PRO", icon: <Crown size={16} /> },
  ];

  return (
    <div className="mx-3 mt-4 mb-6 bg-gradient-to-br from-indigo-50 to-white border border-indigo-100 rounded-3xl p-6 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-indigo-900 flex items-center gap-2">
            Referral Milestones <SparkleIcon />
          </h2>
          <p className="text-sm text-indigo-600">Invite friends & unlock Arthereum Pro for FREE!</p>
        </div>
        <div className="bg-white/80 backdrop-blur-sm border border-indigo-200 px-4 py-2 rounded-2xl shadow-sm">
          <span className="block text-[10px] text-indigo-400 uppercase font-black tracking-widest">Total Invites</span>
          <span className="text-2xl font-black text-indigo-700 font-mono">{referralCount}</span>
        </div>
      </div>

      <div className="space-y-4">
        {milestones.map((m, idx) => {
          const isUnlocked = referralCount >= m.count;
          const progress = Math.min(100, (referralCount / m.count) * 100);

          return (
            <div key={idx} className={`relative p-4 rounded-2xl border transition-all ${isUnlocked ? "bg-indigo-600 border-indigo-700 shadow-md translate-x-1" : "bg-white border-indigo-100 opacity-80"}`}>
              <div className="flex items-center justify-between relative z-10">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isUnlocked ? "bg-white text-indigo-600" : "bg-indigo-50 text-indigo-400"}`}>
                    {m.icon}
                  </div>
                  <div>
                    <h4 className={`font-bold ${isUnlocked ? "text-white" : "text-indigo-900"}`}>{m.label}</h4>
                    <p className={`text-[11px] ${isUnlocked ? "text-indigo-100" : "text-indigo-400"}`}>
                      {isUnlocked ? "Goal Achieved! ✨" : `${m.count - referralCount} more to unlock`}
                    </p>
                  </div>
                </div>
                {!isUnlocked && (
                  <div className="text-right">
                    <span className="text-xs font-bold text-indigo-300 font-mono">{Math.floor(progress)}%</span>
                  </div>
                )}
              </div>
              
              {!isUnlocked && (
                <div className="mt-3 relative w-full h-1.5 bg-indigo-50 rounded-full overflow-hidden">
                   <div 
                    className="absolute top-0 left-0 h-full bg-indigo-300 transition-all duration-700"
                    style={{ width: `${progress}%` }}
                   />
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      <div className="mt-6 pt-6 border-t border-indigo-100/50">
        <div className="flex items-center justify-between mb-4">
           <div className="flex flex-col">
             <span className="text-[10px] text-indigo-400 font-bold uppercase">Reward Value</span>
             <span className="text-sm font-black text-indigo-900">
               {referralCount * 200} ATH {referralCount > 0 ? `+ ${referralCount * 5} Pi` : ""} Earned
             </span>
           </div>
           <div className="flex flex-col text-right">
             <span className="text-[10px] text-indigo-400 font-bold uppercase">Current Multiplier</span>
             <span className="text-sm font-black text-emerald-600">1.0x (Standard)</span>
           </div>
        </div>
        
        <div className={`p-3 rounded-2xl bg-indigo-600 text-white flex items-center gap-3 overflow-hidden relative`}>
          <div className="absolute -right-2 -top-2 opacity-20">
            <Users size={40} />
          </div>
          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-lg animate-bounce">
            💡
          </div>
          <div className="relative z-10">
            <p className="text-[10px] font-black uppercase opacity-80">Pro Tip</p>
            <p className="text-[11px] font-bold leading-tight">
              Start an invite streak! Invite 1 friend today for <span className="text-yellow-300">+10% bonus</span> rewards tomorrow.
            </p>
          </div>
        </div>
      </div>
      
      <p className="mt-4 text-[10px] text-indigo-400 text-center italic">
        * Pro status boosts all your mining grants by 2x permanently.
      </p>
    </div>
  );
};

const SparkleIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/>
        <path d="M5 3v4"/><path d="M19 17v4"/><path d="M3 5h4"/><path d="M17 19h4"/>
    </svg>
)

export default ReferralMilestones;
